import React, { useState, useEffect } from "react";
import {
    useTheme,
    Box,
    Typography,
    Card,
    Button,
    Grid,
    Container,
    LinearProgress,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from "react-redux";
import useFetch from '../../hooks/useFetch';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FavoriteIcon from '@mui/icons-material/Favorite';
import SpaIcon from '@mui/icons-material/Spa';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import GrainIcon from '@mui/icons-material/Grain';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsIcon from '@mui/icons-material/Groups';
import { alpha } from "@mui/material";
import AirIcon from "@mui/icons-material/Air";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { teal } from "@mui/material/colors";

// Turns a plain YouTube/Vimeo link (what a content editor would naturally
// paste into the CMS) into the iframe-embeddable form.
const toEmbedUrl = (url) => {
    if (!url) return null;
    try {
        const youtubeMatch = url.match(
            /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/
        );
        if (youtubeMatch) return `https://www.youtube.com/embed/${youtubeMatch[1]}`;

        const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
        if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

        return url; // already an embeddable/self-hosted URL
    } catch {
        return url;
    }
};

const About = () => {
    const theme = useTheme();
    const locale = useSelector(state => state.language.locale);
    const { data } = useFetch(`/abouts?[filters][locale][$eq]=${locale}&populate=*`);
    const about = data?.[0] ?? {};
    const colors = theme.palette.about;
    const uploadedVideoUrl = about?.video?.url
        ? process.env.REACT_APP_UPLOAD_URL + about.video.url
        : null;
    const externalVideoEmbedUrl = !uploadedVideoUrl ? toEmbedUrl(about?.videoUrl) : null;
    const hasVideo = Boolean(uploadedVideoUrl || externalVideoEmbedUrl);

    const [scrollY, setScrollY] = useState(0);
    const [counters, setCounters] = useState({
        customers: 0,
        products: 0,
        years: 0,
        satisfaction: 0
    });

    // Parallax effect
    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Counter animation
    useEffect(() => {
        const targetCounters = { customers: 50000, products: 500, years: 3, satisfaction: 98 };
        const intervals = Object.keys(targetCounters).map(key => {
            return setInterval(() => {
                setCounters(prev => ({
                    ...prev,
                    [key]: Math.min(prev[key] + targetCounters[key] / 50, targetCounters[key])
                }));
            }, 30);
        });
        return () => intervals.forEach(clearInterval);
    }, []);

    const values = [
        {
            icon: CheckCircleIcon,
            title: "Jakość",
            desc: about.qualityDesc || "Premium składniki i mistrzowskie kompozycje zapachowe w przystępnej cenie.",
            color: theme.palette.about.light,
            bgColor: theme.palette.about.light
        },
        {
            icon: ShoppingBagIcon,
            title: "Swoboda",
            desc: about.freedomDesc || "Swoboda wyboru spośród setek unikalnych zapachów dopasowanych do Twojego stylu.",
            color: theme.palette.about.light,
            bgColor: theme.palette.about.light
        },
        {
            icon: FavoriteIcon,
            title: "Dostępność",
            desc: about.accessDesc || "Luksusowe zapachy bez luksu cenowego - piękno dostępne dla wszystkich.",
            color: theme.palette.about.light,
            bgColor: theme.palette.about.light
        },
        {
            icon: SpaIcon,
            title: "Ekologiczność",
            desc: about.ecologyDesc || "Dbamy o planetę poprzez zrównoważone opakowania i etyczne składniki.",
            color: theme.palette.about.light,
            bgColor: theme.palette.about.light
        }
    ];

    const stats = [
        { label: "Zadowoleni Klienci", value: counters.customers, suffix: "+" },
        { label: "Unikalne Zapachy", value: counters.products, suffix: "+" },
        { label: "Lat Doświadczenia", value: counters.years, suffix: "" },
        { label: "Zadowolenie", value: counters.satisfaction, suffix: "%" }
    ];
    const senses = [
        {
            title: "Zapach",
            desc: "Precyzyjnie skomponowane nuty zapachowe",
            icon: AirIcon,
        },
        {
            title: "Wygląd",
            desc: "Eleganckie butelki do kolekcji",
            icon: VisibilityIcon,
        },
        {
            title: "Doświadczenie",
            desc: "Podróż zmysłowa z każdym sprejem",
            icon: AutoAwesomeIcon,
        },
        {
            title: "Emocja",
            desc: "Zapachy, które radzą ci się czuć",
            icon: FavoriteIcon,
        },
        {
            title: "Trwałość",
            desc: "Zapach, który przetrwa cały dzień",
            icon: AccessTimeIcon,
        }
    ];

    return (
        <Box sx={{ width: "100%", overflow: "hidden", alignContent: "center" }}>
            {/* ============ HERO SECTION ============ */}
            <Box
                sx={{
                    position: "relative",
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: `linear-gradient(135deg, ${colors.gradientStart}15, ${colors.gradientEnd}15)`,
                    overflow: "hidden",
                    alignItems: "center"
                }}
            >
                {/* Animated background circles */}
                <Box
                    sx={{
                        position: "absolute",
                        width: 400,
                        height: 400,
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${colors.primary}30, transparent)`,
                        top: -200,
                        right: -100,
                        display: "none",
                        alignItems: "center"
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        width: 300,
                        height: 300,
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${colors.secondary}30, transparent)`,
                        bottom: -150,
                        left: -100,
                        display: "none",
                        animationDelay: "1s",
                        alignItems: "center"
                    }}
                />

                <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2, py: 10 }}>
                    <Grid container spacing={6} alignItems="center">
                        {/* TEXT CONTENT */}
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    animation: "slideInUp 0.8s ease-out",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "100%"

                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: { xs: "1rem", md: "1.2rem" },
                                        color: theme.palette.about.primary,
                                        fontWeight: 500,
                                        letterSpacing: "0.1em",
                                        textTransform: "uppercase",
                                        mb: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 1
                                    }}
                                >
                                    Poznaj Naszą Historię
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: { xs: "2.5rem", md: "4rem" },
                                        fontWeight: 300,
                                        fontFamily: 'var(--font-serif)',
                                        lineHeight: 1.1,
                                        mb: 3,
                                        color: colors.dark,
                                        background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                                        backgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                        WebkitBackgroundClip: "text",
                                        alignItems: "center",
                                        justifyContent: "center"

                                    }}
                                >
                                    Zapachy, które opowiadają Historię
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: { xs: "1.1rem", md: "1rem" },
                                        fontWeight: 500,
                                        lineHeight: 1.1,
                                        mb: 3,
                                        color: colors.dark,
                                        background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                                        backgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                        WebkitBackgroundClip: "text",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"

                                    }}
                                >
                                    {about.tagline || "Tworzymy zapachy, które transcendują zwyczajne. Każda butelka to dzieło sztuki, każda nota to emocja."}
                                </Typography>

                                {/* <Button
                                    component={RouterLink}
                                    to="/products"
                                    size="large"
                                    variant="outlined"

                                    sx={{
                                        fontSize: { xs: "1.1rem", md: "1rem" },
                                        fontWeight: 500,
                                        lineHeight: 1.1,
                                        mb: 4,
                                        color: colors.dark,
                                        background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                                        backgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                        WebkitBackgroundClip: "text",
                                        display: "flex",
                                        width: "fit-content",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: "12px 24px",
                                        borderRadius: 0,
                                        border: `2px solid ${colors.primary}`,
                                        boxShadow: `0 8px 16px ${alpha(colors.primary, 0.2)}`,
                                        textTransform: "none",
                                        transition: "all 0.3s ease",
                                        transform: "translateX(50%)",

                                        "&:hover": {
                                            WebkitTextFillColor: colors.light,
                                            background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`
                                        }

                                    }}
                                >
                                    Odkryj Kolekcję
                                </Button> */}
                            </Box>
                        </Grid>

                        {/* HERO IMAGE */}
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    perspective: "1000px",
                                    height: { xs: 400, md: 500 },
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    alignItems: "center"

                                }}
                            >
                                {about?.img?.url && (
                                    <Box
                                        component="img"
                                        src={process.env.REACT_APP_UPLOAD_URL + about.img.url}
                                        alt=""
                                        sx={{
                                            width: "90%",
                                            height: "auto",
                                            borderRadius: 0,
                                            boxShadow: `0 40px 80px ${alpha(colors.primary, 0.3)}`,
                                            transition: "transform 0.6s ease",
                                            // base state: 10% to the right + scroll parallax on Y
                                            transform: `translateX(10%) translateY(${scrollY * 0.3}px)`,
                                            animation: "slideInRight 0.8s ease-out",
                                            "&:hover": {
                                                // keep the same X + Y, just add scale
                                                transform: `scale(1.05) translateX(10%) translateY(${scrollY * 0.3}px)`
                                            }
                                        }}
                                    />

                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Container>

                {/* Scroll indicator */}
                <Box
                    sx={{
                        position: "absolute",
                        bottom: 30,
                        left: "50%",
                        transform: "translateX(-50%)",
                        animation: "bounce 2s ease-in-out infinite"
                    }}
                >
                    <Typography sx={{ fontSize: "0.9rem", color: colors.primary, mb: 1 }}>
                        Scroll
                    </Typography>
                    <Box
                        sx={{
                            width: 24,
                            height: 40,
                            border: `2px solid ${colors.primary}`,
                            borderRadius: 0,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "8px"
                        }}
                    >
                        <Box
                            sx={{
                                width: 2,
                                height: 10,
                                background: colors.primary,
                                borderRadius: 0,
                                animation: "slide 2s ease-in-out infinite"
                            }}
                        />
                    </Box>
                </Box>

                {/* Keyframe animations */}
                <style>{`
                    @keyframes float {
                        0%, 100% { transform: translateY(0px); }
                        50% { transform: translateY(30px); }
                    }
                    @keyframes slideInUp {
                        from {
                            opacity: 0;
                            transform: translateY(40px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    @keyframes slideInRight {
                        from {
                            opacity: 0;
                            transform: translateX(40px);
                        }
                        to {
                            opacity: 1;
                            transform: translateX(0);
                        }
                    }
                    @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.5; }
                    }
                    @keyframes bounce {
                        0%, 100% { transform: translateX(-50%) translateY(0); }
                        50% { transform: translateX(-50%) translateY(10px); }
                    }
                    @keyframes slide {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(12px); }
                    }
                `}</style>
            </Box>

            {/* ============ STORY SECTION ============ */}
            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
                <Box sx={{ textAlign: "center", mb: 10 }}>
                    <Typography
                        sx={{
                            fontSize: { xs: "0.95rem", md: "1.1rem" },
                            color: colors.primary,
                            fontWeight: 500,
                            textTransform: "uppercase",
                            letterSpacing: "0.1em",
                            mb: 2
                        }}
                    >
                        Nasza Historia
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: { xs: "2rem", md: "3.5rem" },
                            fontWeight: 300,
                                        fontFamily: 'var(--font-serif)',
                            color: colors.dark,
                            mb: 4,
                            position: "relative",
                            display: "inline-block",
                            "&:after": {
                                content: '""',
                                position: "absolute",
                                bottom: -15,
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: 80,
                                height: 4,
                                background: `linear-gradient(90deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                                borderRadius: 0
                            }
                        }}
                    >
                        Od Marzenia do Rzeczywistości
                    </Typography>
                </Box>

                {about.storyDesc && (
                    <Grid container spacing={6} sx={{ mt: 4 }}>
                        <Grid item xs={12}>
                            <Card
                                sx={{
                                    p: { xs: 4, md: 6 },
                                    background: `linear-gradient(135deg, ${colors.light}80, ${alpha(colors.secondary, 0.1)})`,
                                    borderRadius: 0,
                                    border: `2px solid ${alpha(colors.primary, 0.2)}`,
                                    boxShadow: "none",
                                    transition: "all 0.3s ease",
                                    alignItems: "center",

                                    "&:hover": {
                                        borderColor: colors.primary,
                                        boxShadow: `0 20px 40px ${alpha(colors.primary, 0.15)}`
                                    }
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: { xs: "1.1rem", md: "1.3rem" },
                                        lineHeight: 1.8,
                                        color: colors.dark,
                                        fontWeight: 500,
                                        alignItems: "center"
                                    }}
                                >
                                    {about.storyDesc}
                                </Typography>
                            </Card>
                        </Grid>
                    </Grid>
                )}
            </Container>

            {/* ============ STORY VIDEO SECTION ============ */}
            {hasVideo && (
                <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 12 } }}>
                    <Box sx={{ textAlign: "center", mb: 6 }}>
                        <Typography
                            sx={{
                                fontSize: { xs: "0.95rem", md: "1.1rem" },
                                color: colors.primary,
                                fontWeight: 500,
                                textTransform: "uppercase",
                                letterSpacing: "0.1em",
                                mb: 2
                            }}
                        >
                            Zobacz nas w akcji
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: { xs: "1.8rem", md: "2.8rem" },
                                fontWeight: 300,
                                        fontFamily: 'var(--font-serif)',
                                color: colors.dark
                            }}
                        >
                            Nasza Historia w Filmie
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            position: "relative",
                            width: "100%",
                            maxWidth: 960,
                            mx: "auto",
                            aspectRatio: "16 / 9",
                            borderRadius: 0,
                            overflow: "hidden",
                            boxShadow: `0 30px 60px ${alpha(colors.primary, 0.25)}`,
                            bgcolor: "#000",
                        }}
                    >
                        {uploadedVideoUrl ? (
                            <Box
                                component="video"
                                src={uploadedVideoUrl}
                                controls
                                playsInline
                                sx={{ width: "100%", height: "100%", display: "block", objectFit: "cover" }}
                            />
                        ) : (
                            <Box
                                component="iframe"
                                src={externalVideoEmbedUrl}
                                title="Carlo Bossi - nasza historia"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                sx={{ width: "100%", height: "100%", border: "none", display: "block" }}
                            />
                        )}
                    </Box>
                </Container>
            )}

            {/* ============ VALUES SECTION ============ */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${alpha(colors.primary, 0.08)}, ${alpha(colors.secondary, 0.08)})`,
                    py: { xs: 8, md: 12 },
                    position: "relative",
                    overflow: "hidden",
                    alignItems: "center"
                }}
            >
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: "center", mb: 10 }}>
                        <Typography
                            sx={{
                                fontSize: { xs: "0.95rem", md: "1.1rem" },
                                color: colors.primary,
                                fontWeight: 500,
                                textTransform: "uppercase",
                                letterSpacing: "0.1em",
                                mb: 2,
                                alignItems: "center"
                            }}
                        >
                            Czym się wyróżniamy
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: { xs: "2rem", md: "3.5rem" },
                                fontWeight: 300,
                                        fontFamily: 'var(--font-serif)',
                                color: colors.dark,
                                position: "relative",
                                display: "inline-block",
                                "&:after": {
                                    content: '""',
                                    position: "absolute",
                                    bottom: -15,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    width: 80,
                                    height: 4,
                                    background: `linear-gradient(90deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                                    borderRadius: 0,

                                }
                            }}
                        >
                            Nasze Wartości
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            mt: 6,
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "1fr 1fr",
                                md: "repeat(4, 1fr)" // always four in one row on desktop
                            },
                            gap: 4,
                            alignItems: "center"
                        }}
                    >
                        {values.map((value, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Card
                                    sx={{
                                        p: 4,
                                        height: "100%",
                                        borderRadius: 0,
                                        border: `3px solid transparent`,
                                        background: value.bgColor,
                                        boxShadow: "none",
                                        transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
                                        cursor: "pointer",
                                        position: "relative",
                                        overflow: "hidden",
                                        alignItems: "center",
                                        "&:before": {
                                            content: '""',
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: "3px",
                                            background: value.color,
                                            transform: "scaleX(0)",
                                            transformOrigin: "left",
                                            transition: "transform 0.4s ease"
                                        },
                                        "&:hover": {
                                            transform: "translateY(-10px)",
                                            boxShadow: `0 25px 50px ${alpha(value.color, 0.25)}`,
                                            borderColor: value.color,
                                            "&:before": {
                                                transform: "scaleX(1)"
                                            }
                                        }
                                    }}
                                >
                                    {/* Icon container */}
                                    <Box
                                        sx={{
                                            height: 70,
                                            borderRadius: 0,
                                            background: value.color,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mb: 3,

                                            transition: "transform 0.3s ease",
                                            ".card:hover &": {
                                                transform: "scale(1.1) rotate(5deg)"
                                            }
                                        }}
                                    >
                                        <value.icon sx={{ fontSize: 40, fill: theme.palette.about.dark, color: theme.palette.primary.main, alignItems: "center" }} />
                                    </Box>

                                    <Typography
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: "1.3rem",
                                            textAlign: "center",
                                            mb: 1.5,
                                            color: theme.palette.about.dark
                                        }}
                                    >
                                        {value.title}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: colors.dark,
                                            opacity: 0.75,
                                            lineHeight: 1.6,
                                            textAlign: "center",
                                            fontSize: "0.95rem"
                                        }}
                                    >
                                        {value.desc}
                                    </Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Box>
                </Container>
            </Box>

            {/* ============ SENSORY JOURNEY SECTION ============ */}
            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
                <Box sx={{ textAlign: "center", mb: 10 }}>
                    <Typography
                        sx={{
                            fontSize: { xs: "0.95rem", md: "1.1rem" },
                            color: colors.primary,
                            fontWeight: 500,
                            textTransform: "uppercase",
                            letterSpacing: "0.1em",
                            mb: 2
                        }}
                    >
                        Sensoryczna Podróż
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: { xs: "2rem", md: "3.5rem" },
                            fontWeight: 300,
                                        fontFamily: 'var(--font-serif)',
                            color: colors.dark,
                            position: "relative",
                            display: "inline-block",
                            "&:after": {
                                content: '""',
                                position: "absolute",
                                bottom: -15,
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: 80,
                                height: 4,
                                background: `linear-gradient(90deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                                borderRadius: 0
                            }
                        }}
                    >
                        Pięć Zmysłów Zapachu
                    </Typography>
                </Box>

                <Box
                    sx={{
                        mt: 6,
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "1fr 1fr",
                            md: "repeat(5, 1fr)" // always five in one row on desktop
                        },
                        gap: 4,
                        alignItems: "center"
                    }}
                >                    {senses.map((sense, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}
                        sx={{
                            p: 4,
                            height: "100%",

                            borderRadius: 0,
                            border: `3px solid transparent`,
                            boxShadow: "none",
                            transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
                            cursor: "pointer",
                            position: "relative",
                            overflow: "hidden",
                            alignItems: "center",
                            "&:before": {
                                content: '""',
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                height: "3px",
                                transform: "scaleX(0)",
                                transformOrigin: "left",
                                transition: "transform 0.4s ease"
                            },
                            "&:hover": {
                                transform: "translateY(-10px)",
                                "&:before": {
                                    transform: "scaleX(1)"
                                }
                            }
                        }}>

                        <sense.icon sx={{ fontSize: 40, color: colors.primary }} />   {/* <-- IMPORTANT */}
                        <Typography
                            sx={{
                                fontWeight: 500,
                                fontSize: "1.1rem",
                                color: colors.dark,
                                mb: 1
                            }}
                        >
                            {sense.title}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "0.9rem",
                                color: colors.dark,
                                opacity: 0.7,
                                lineHeight: 1.5
                            }}
                        >
                            {sense.desc}
                        </Typography>
                    </Grid>
                ))}
                </Box>
            </Container>

            {/* ============ STATS SECTION ============ */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                    py: { xs: 8, md: 10 },
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={16} sx={{ alignItems: "center", justifyContent: "center" }}>
                        {stats.map((stat, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Box sx={{
                                    textAlign: "center", color: "#fff", alignItems: "center",


                                }}>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: "2rem", md: "2.5rem" },
                                            fontWeight: 300,
                                        fontFamily: 'var(--font-serif)',
                                            mb: 0.5,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 0.5,
                                            textAlign: "center"

                                        }}
                                    >
                                        {Math.floor(stat.value).toLocaleString()}
                                        <Box sx={{ fontSize: "1.5rem" }}>
                                            {stat.suffix}
                                        </Box>
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: "1rem",
                                            opacity: 0.9,
                                            fontWeight: 500
                                        }}
                                    >
                                        {stat.label}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ============ QUOTE SECTION ============ */}
            {about.quote && (
                <Box
                    sx={{
                        py: { xs: 8, md: 12 },
                        px: 4,
                        textAlign: "center",
                        position: "relative",
                        overflow: "hidden"
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            top: -100,
                            right: -100,
                            width: 300,
                            height: 300,
                            borderRadius: "50%",
                            background: `linear-gradient(135deg, ${colors.primary}10, transparent)`,
                            zIndex: 0
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: -50,
                            left: -50,
                            width: 250,
                            height: 250,
                            borderRadius: "50%",
                            background: `linear-gradient(135deg, ${colors.secondary}10, transparent)`,
                            zIndex: 0
                        }}
                    />

                    <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
                        <Typography
                            sx={{
                                fontSize: { xs: "1.8rem", md: "3rem" },
                                lineHeight: 1.4,
                                color: colors.dark,
                                fontStyle: "italic",
                                fontWeight: 500,
                                mb: 3
                            }}
                        >
                            "{about.quote}"
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "1.1rem",
                                color: colors.primary,
                                fontWeight: 500,
                                letterSpacing: "0.05em"
                            }}
                        >
                            Nasza Filozofia
                        </Typography>
                    </Container>
                </Box>
            )}

            {/* ============ TEAM SECTION ============ */}
            {about.teamDesc && (
                <Box
                    sx={{
                        background: `linear-gradient(135deg, ${alpha(colors.primary, 0.08)}, ${alpha(colors.secondary, 0.08)})`,
                        py: { xs: 8, md: 12 }
                    }}
                >
                    <Container maxWidth="md">
                        <Box sx={{ textAlign: "center", mb: 6 }}>
                            <Typography
                                sx={{
                                    fontSize: { xs: "0.95rem", md: "1.1rem" },
                                    color: colors.primary,
                                    fontWeight: 500,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.1em",
                                    mb: 2
                                }}
                            >
                                Nasz Zespół
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: { xs: "2rem", md: "3rem" },
                                    fontWeight: 300,
                                        fontFamily: 'var(--font-serif)',
                                    color: colors.dark,
                                    position: "relative",
                                    display: "inline-block",
                                    "&:after": {
                                        content: '""',
                                        position: "absolute",
                                        bottom: -15,
                                        left: "50%",
                                        transform: "translateX(-50%)",
                                        width: 80,
                                        height: 4,
                                        background: `linear-gradient(90deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                                        borderRadius: 0
                                    }
                                }}
                            >
                                Ludzie za Magią
                            </Typography>
                        </Box>

                        <Card
                            sx={{
                                p: { xs: 4, md: 6 },
                                borderRadius: 0,
                                border: `2px solid ${alpha(colors.primary, 0.2)}`,
                                background: "#fff",
                                boxShadow: `0 20px 40px ${alpha(colors.primary, 0.1)}`,
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    borderColor: colors.primary,
                                    boxShadow: `0 30px 60px ${alpha(colors.primary, 0.15)}`
                                }
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: { xs: "1.05rem", md: "1.25rem" },
                                    lineHeight: 1.8,
                                    color: colors.dark,
                                    fontWeight: 500
                                }}
                            >
                                {about.teamDesc}
                            </Typography>
                        </Card>
                    </Container>
                </Box>
            )}

            {/* ============ FINAL CTA SECTION ============ */}
            <Box
                sx={{
                    position: "relative",
                    py: { xs: 10, md: 14 },
                    px: 4,
                    textAlign: "center",
                    overflow: "hidden"
                }}
            >
                {/* Background elements */}
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `linear-gradient(135deg, ${alpha(colors.primary, 0.05)}, ${alpha(colors.secondary, 0.05)})`,
                        zIndex: -1
                    }}
                />

                <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
                    <Typography
                        sx={{
                            fontSize: { xs: "0.95rem", md: "1.1rem" },
                            color: colors.primary,
                            fontWeight: 500,
                            textTransform: "uppercase",
                            letterSpacing: "0.1em",
                            mb: 2
                        }}
                    >
                        Gotowy?
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: { xs: "2rem", md: "3.5rem" },
                            fontWeight: 300,
                                        fontFamily: 'var(--font-serif)',
                            color: colors.dark,
                            mb: 3,
                            position: "relative",
                            display: "inline-block",
                            "&:after": {
                                content: '""',
                                position: "absolute",
                                bottom: -15,
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: 80,
                                height: 4,
                                background: `linear-gradient(90deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                                borderRadius: 0
                            }
                        }}
                    >
                        Znajdź Swój Zapach
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: "1.15rem",
                            color: colors.dark,
                            opacity: 0.75,
                            lineHeight: 1.8,
                            mb: 6,
                            mt: 4
                        }}
                    >
                        Dołącz do tysięcy klientów, którzy odkryli idealne zapachy dla siebie. Twoja
                        zapachowa podróż zaczyna się tutaj.
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            flexDirection: { xs: "column", sm: "row" },
                            justifyContent: "center"
                        }}
                    >
                        <Button
                            component={RouterLink}
                            to="/products"
                            variant="contained"
                            sx={{ px: 5, py: 2 }}
                        >
                            Przeglądaj Kolekcję
                        </Button>
                        {/* <Button
                            size="large"
                            sx={{
                                px: 6,
                                py: 2.5,
                                borderRadius: 0,
                                textTransform: "none",
                                fontWeight: 500,
                                fontSize: "1.05rem",
                                border: `2px solid ${colors.primary}`,
                                color: colors.primary,
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    background: colors.primary,
                                    color: "#fff",
                                    transform: "translateY(-5px)"
                                }
                            }}
                        >
                            Dowiedz się Więcej
                        </Button> */}
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default About;